import React, { useEffect, useState, useMemo, useCallback } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/bulkPurchase/addDeviceDialogStyle';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import { EshopProductsService } from 'services/eshop/products';
import { catchError, mergeMap, finalize, map } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import { VouchersService } from 'services/vouchers/vouchers';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import { getSmartVoucherError } from '../common/commons';
import { IAvailableColorWithId, IAvailableValueItem, IItem, IProductByIdItem } from 'utils/api/models/eshop/products';
import { BulkNumber } from '.';
import { ISmartVoucher } from 'utils/api/models/vouchers/getSmartVoucher';
import { ICalculatedProduct } from 'utils/api/models/vouchers/calculateInstallment';
import DeviceCard from '../subcomponents/DeviceCard/DeviceCard';
import Button from 'components/CustomButtons/Button';
import { getValidateOfferPayload } from 'utils/api/payloads/eshopApi';
import { userIdSelector } from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { allAccountSelector } from 'redux/modules/addServices/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDevice {
	name: string;
	id: string;
}
interface IAddDeviceDialog {
	isOpen: boolean;
	hideDialog: () => void;
	number?: BulkNumber | undefined;
	onAddToCart: () => void;

}

const configuration = {
	emfConfigId: '13',
	orderType: 'SMART_VOUCHER',
	subscriptionType: 'Voice',
	packageGroup: 'DEVICE_CORP_INSTALLMENT',
};

const CSTMERR_ORDERITEM_SERVICE_UNAVAILABLE_KEY = '_CSTMERR_ORDERITEM_SERVICE_UNAVAILABLE';
const CSTMERR_ORDERITEM_MAX_ONE_COMMITMENT_PER_NUMBER_KEY = '_CSTMERR_ORDERITEM_MAX_ONE_COMMITMENT_PER_NUMBER';
const ERR_INVALID_COOKIE_KEY = '_ERR_INVALID_COOKIE';
const ERR_BAD_REQUEST_KEY = '_ERR_BAD_REQUEST';

const AddDeviceDialog = ({ isOpen, hideDialog, number, onAddToCart}: IAddDeviceDialog) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedDevice, setSelectedDevice] = useState<string>('');
	const [smartVoucherError, setSmartVoucherError] = useState<string>('');
	const [devices, setDevices] = useState<IDevice[]>([]);
	const [responseDevices, setResponseDevices] = useState<IItem[]>([]);
	const [commitments, setCommitments] = useState<ISmartVoucher[]>([]);
	const [selectedCommitment, setSelectedCommitment] = useState<ISmartVoucher>();
	const [selectedDeviceVariant, setSelectedDeviceVariant] = useState<IProductByIdItem>();
	const [calculatedProductPrices, setCalculatedProductPrices] = useState<ICalculatedProduct>();
	const [deviceAvailability, setDeviceAvailability] = useState<boolean>(false);
	const [addToCartError, setAddToCartError] = useState<boolean>(false);
	const [addToCartErrorMessage, setAddToCartErrorMessage] = useState<string>('');
	const classes = useStyles();

	const selectedDeviceName = devices.find((device) => device?.id === selectedDevice)?.name;

	const [productItems, setProductItems] = useState<IProductByIdItem[]>([]);
	const [selectedColorId, setSelectedColorId] = useState<string>();
	const [selectedCapacityId, setSelectedCapacityId] = useState<string>();
	const [availableDeviceColors, setAvailableDeviceColors] = useState<IAvailableColorWithId[]>([]);
	const [availableDeviceCapacity, setAvailableDeviceCapacity] = useState<IAvailableValueItem[]>([]);

	const userId = useSelector(userIdSelector);
	const corpAccounts = useSelector(allAccountSelector);
	const findProperProduct = useCallback(
		({ item }: { item: any }) => {
			const matchSelectedColor = selectedColorId && item.colorId === selectedColorId;
			const matchSelectedCapacity = selectedCapacityId && item.capacityId === selectedCapacityId;
			return matchSelectedColor && matchSelectedCapacity;
		},
		[selectedCapacityId, selectedColorId]
	);

	const findCatEntry = useCallback(
		(items: IProductByIdItem[]) => {
			return items?.find((item) => findProperProduct({ item }));
		},
		[findProperProduct]
	);

	const calculatePrices = useCallback(() => {
		setLoading(true);
		setAddToCartError(false);
		const upFrontCommitment = (selectedCommitment?.installments || []).find(
			(installment) => installment?.duration === 0
		);

		VouchersService.calculateInstallment({
			accountNo: number?.accountId,
			emfConfigId: configuration?.emfConfigId,
			orderType: configuration?.orderType,
			subscriptionType: configuration?.subscriptionType,
			packageGroup: configuration?.packageGroup,
			productId: String(upFrontCommitment?.productOfferId) || '',
			serviceCode: selectedDeviceVariant?.partNumber,
			tariffType: selectedDeviceVariant?.partNumber,
			commitmentAmount: String(selectedCommitment?.deviceDiscount),
			upfrontCollection: String(selectedCommitment?.upFrontPayment),
			commitmentDuration: String(selectedCommitment?.commitmentDuration),
			installmentDuration: String(upFrontCommitment?.duration),
		})
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(
				({ calculatedProduct }) => {
					setCalculatedProductPrices(calculatedProduct);
				},
				(error) => throwError(error)
			);
	}, [number, selectedCommitment, selectedDeviceVariant]);

	const addToCart = () => {
		setAddToCartError(false);
		setLoading(true);
		EshopProductsService.validateCart(
			getValidateOfferPayload({
				calculatedProduct: calculatedProductPrices,
				mobilePhone: number?.accountNumber || '',
				commitment: calculatedProductPrices?.device?.productId,
				smartVoucherCommitmentId: calculatedProductPrices?.discount?.productId,
			})
		).subscribe(
			(validate) => {
				if (validate) {
					setAddToCartError(false);
					addToCartRequest();
				} else {
					setLoading(false);
					setAddToCartError(true);
					return EMPTY;
				}
			},
			(error) => {
				setLoading(false);
				setAddToCartError(true);
				setAddToCartErrorMessage(
					[
						CSTMERR_ORDERITEM_SERVICE_UNAVAILABLE_KEY,
						CSTMERR_ORDERITEM_MAX_ONE_COMMITMENT_PER_NUMBER_KEY,
						ERR_INVALID_COOKIE_KEY,
					].indexOf(String(error?.response?.errors?.[0]?.errorKey)) > -1
						? error.response.errors[0].errorKey
						: ERR_BAD_REQUEST_KEY
				);
				throwError(error);
			}
		);
	};

	const addToCartRequest = () => {
		return EshopProductsService.addItemsToCart([
			{
				quantity: '1',
				productId: selectedDeviceVariant?.id || '',
				xitem_login: userId,
				xitem_planAction: 'B2B_WITH_VOUCHER_UPFRONT',
				xitem_commitmentDuration: String(selectedCommitment?.commitmentDuration),
				xitem_installmentDuration: '0',
				xitem_mobilePhoneType: number?.billingType,
				xitem_mobilePhone: number?.accountNumber,
				xitem_accountNo: number?.accountId,
				xitem_corporateNo: number?.accountNumber,
				installmentPeriod: '',
				xitem_bulk: 'true',
				xitem_ebuPlanName: number?.tariffName,
			},
		])
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(
				() => {
					setAddToCartError(false);
					refreshData();
					onAddToCart();
				},
				(error) => {
					setAddToCartError(true);
					throwError(error);
				}
			);
	};

	const selectColorAndCapacityData = useCallback(
		(productItems: any[] | undefined, clear: boolean) => {
			let catEntry: IProductByIdItem | undefined;
			if (!selectedColorId || !selectedCapacityId || clear) {
				catEntry = productItems?.length && productItems[0];
				setSelectedCapacityId(catEntry?.capacityId);
				setSelectedColorId(catEntry?.colorId);
			} else {
				catEntry = findCatEntry(productItems || []);
			}
			setSelectedDeviceVariant(catEntry);
		},
		[findCatEntry, selectedCapacityId, selectedColorId]
	);

	const getCommitmentsData = useCallback(() => {
		setLoading(true);
		const getOrganization = corpAccounts.find(({ accountId }: any) => accountId === number?.accountId)
		VouchersService.getSmartVoucher(getOrganization ? getOrganization?.documentId.toString() : '', number?.accountNumber || '').subscribe(
			({ vouchers }) => {
				const upFrontPaymentCommitments = (vouchers || []).filter(
					(voucher) => !!(voucher?.installments || []).find((installment) => installment?.duration === 0)
				);

				if (upFrontPaymentCommitments?.length) {
					setCommitments(upFrontPaymentCommitments);
					setSelectedCommitment(upFrontPaymentCommitments?.[0]);
				} else {
					setSmartVoucherError('purchase.bulk.devices.voucher.warning');
				}
			},
			({ response }) => {
				setSmartVoucherError(getSmartVoucherError(response).errorDescription);
			}
		);
	}, [number]);

	const getProductData = useCallback(
		(productId: string) => {
			setLoading(true);
			const product = (responseDevices || []).find((r) => r?.uniqueID === productId);
			const availableProductComponentsResponse = EshopProductsService.getProductById({
				productId,
				itemType: 'ITEM',
			}).pipe(
				mergeMap((dataItems) => {
					setProductItems(dataItems?.items || []);
					getCommitmentsData();
					return EshopProductsService.getAvailableProductComponents({
						product,
						items: dataItems?.items,
					}).pipe(
						map((items) => {
							return { dataItems: dataItems, items: items };
						})
					);
				}),
				catchError((error) => {
					return throwError(error);
				})
			);

			availableProductComponentsResponse.subscribe(
				({ dataItems, items }) => {
					setAvailableDeviceColors(items?.availableColors);
					setAvailableDeviceCapacity(items?.availableCapacity);
					selectColorAndCapacityData(dataItems?.items, true);
				},
				(error) => throwError(error)
			);
		},
		[selectColorAndCapacityData, responseDevices, getCommitmentsData]
	);

	const onColorChange = (colorId: string) => {
		setSelectedColorId(colorId);
	};

	const onCapacityChange = (capacityId: string) => {
		setSelectedCapacityId(capacityId);
	};

	const addToCartDisabled = useMemo(() => {
		return !(
			selectedCapacityId &&
			selectedColorId &&
			selectedCommitment &&
			selectedDeviceVariant &&
			calculatedProductPrices &&
			deviceAvailability
		);
	}, [
		selectedCapacityId,
		selectedColorId,
		selectedCommitment,
		selectedDeviceVariant,
		calculatedProductPrices,
		deviceAvailability,
	]);

	useEffect(() => {
		if (selectedColorId && selectedCapacityId) {
			selectColorAndCapacityData(productItems, false);
		}
	}, [selectedColorId, selectedCapacityId, productItems, selectColorAndCapacityData]);

	useEffect(() => {
		if (selectedCommitment && selectedDeviceVariant) {
			calculatePrices();
		}
	}, [selectedCommitment, selectedDeviceVariant, calculatePrices]);

	useEffect(() => {
		if (number) {
			setLoading(true);
			EshopProductsService.getProductsByParentCategory({
				offset: 0,
			})
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(
					({ items }) => {
						setResponseDevices(items);
						setDevices(
							(items || [])
								.map((item: IItem) => {
									return {
										name: item?.name || '',
										id: item?.uniqueID || '',
									};
								})
								.sort((a: IDevice, b: IDevice) => (a?.name > b?.name ? 1 : -1))
						);
					},
					(error) => throwError(error)
				);
		}
	}, [number]);

	const refreshData = () => {
		setSelectedDevice('');
		setAddToCartError(false);
		setSmartVoucherError('');
		setSelectedDeviceVariant(undefined);
		setCalculatedProductPrices(undefined);
		setCommitments([]);
		hideDialog();
	};

	return (
		<CustomDialog
			header={translate('purchase.bulk.devices.add.device.dialog.title', number?.accountNumber || '')}
			isOpen={isOpen}
			hideDialog={() => {
				refreshData();
			}}
			loading={loading}
			width={568}
			dialogBody={
				<>
					<div className={classes.dialogContainer}>
						<div className={classes.container}>
							<div className="dataContainer">
								<BodyS disableMargin>
									{translate('purchase.bulk.devices.add.device.dialog.description')}
								</BodyS>

								<div className={classes.input}>
									<AlternativeSelect
										loading={!devices?.length}
										items={devices || []}
										value={
											<BodyS>
												{selectedDeviceName ||
													translate('purchase.bulk.devices.add.device.dialog.input.title')}
											</BodyS>
										}
										onClick={(item: any) => {
											setSelectedDevice(item?.id);
											setAddToCartError(false);
											getProductData(item?.id);
										}}
										selectedItems={[selectedDevice || null]}
										className={
											selectedDevice ? classes.alternativeSelected : classes.alternativeUnselected
										}
									/>
								</div>
							</div>
						</div>

						<ConditionalRender
							show={!!selectedDevice}
							onTrue={() => (
								<div className={classes.productContainer}>
									<ConditionalRender
										show={addToCartError}
										onTrue={() => (
											<div className={classes.errorContainer}>
												<ErrorBox text={translate(addToCartErrorMessage)} />
											</div>
										)}
									/>

									<ConditionalRender
										show={!!smartVoucherError}
										onTrue={() => (
											<BodyS disableMargin color="danger">
												{translate(smartVoucherError)}
											</BodyS>
										)}
										onFalse={() => (
											<>
												<ConditionalRender
													show={!!commitments?.length}
													onTrue={() => (
														<ConditionalRender
															show={commitments?.length > 1}
															onTrue={() => (
																<>
																	<div className={classes.titleContainer}>
																		<BodySBold disableMargin>
																			{translate(
																				'purchase.bulk.devices.add.device.dialog.commitment.description'
																			)}
																		</BodySBold>
																	</div>
																	{(commitments || []).map((commitment, index) => (
																		<CustomRadio
																			key={`${commitment?.commitmentDuration}${index}`}
																			label={translate(
																				'purchase.bulk.devices.add.device.dialog.commitment.period',
																				commitment?.commitmentDuration
																			)}
																			checked={
																				selectedCommitment?.commitmentDuration ===
																				commitment?.commitmentDuration
																			}
																			onChange={() => {
																				setSelectedCommitment(commitment);
																			}}
																		/>
																	))}
																</>
															)}
															onFalse={() => (
																<>
																	<BodyS inline disableMargin>
																		{translate(
																			'purchase.bulk.devices.add.device.dialog.commitment.description.text'
																		)}
																	</BodyS>
																	<BodySBold inline disableMargin>
																		{translate(
																			'purchase.bulk.devices.add.device.dialog.commitment.period',
																			selectedCommitment?.commitmentDuration || ''
																		)}
																	</BodySBold>
																</>
															)}
														/>
													)}
												/>
											</>
										)}
									/>
								</div>
							)}
						/>

						<ConditionalRender
							show={!!calculatedProductPrices}
							onTrue={() => (
								<DeviceCard
									key={'device-card'}
									availableColors={availableDeviceColors}
									calculatedProduct={calculatedProductPrices}
									availableCapacity={availableDeviceCapacity}
									productVariant={selectedDeviceVariant}
									onColorChange={onColorChange}
									onCapacityChange={onCapacityChange}
									onAvailableDevice={setDeviceAvailability}
								/>
							)}
						/>
					</div>
					<div className={classes.right}>
						<Button
							color="primary-light"
							disabled={addToCartDisabled}
							onClick={addToCart}
							className={classes.nextButton}
						>
							{translate('purchase.bulk.devices.add.to.cart')}
						</Button>
					</div>
				</>
			}
		/>
	);
};

export default AddDeviceDialog;
